/**
 * Una cifra grande que **siempre cabe en su caja**.
 *
 * Los paneles de resumen enseñan un número en grande: lo que se debe, lo que
 * está vencido, cuántas recepciones esperan. El tamaño se elige mirando un
 * número de ejemplo, y ahí es donde falla: `4.898.000,00` cabe y
 * `1.234.567.890,00` no, y en un móvil no cabe casi ninguno.
 *
 * Lo que hace el navegador cuando no cabe es de las dos maneras malas: parte el
 * número en dos líneas -y `4.898` encima de `.000,00` no es una cifra, son dos
 * cosas- o lo desborda y se sale de la tarjeta.
 *
 * Así que se mide y se encoge. Nada de adivinar con `@media`: lo que decide no
 * es el ancho de la pantalla sino **cuántos dígitos tiene el número**, y eso no
 * lo sabe una consulta de medios.
 *
 * ```html
 * <span class="company-cifra h3 mb-0">4.898.000,00</span>
 * ```
 *
 * Se vuelve a medir sola cuando cambia el ancho, cuando cambia el número, y
 * cuando acaban de cargar las fuentes -que es el caso que se olvida: la primera
 * medida se toma con la fuente de reserva, y Montserrat no ocupa lo mismo-.
 */

/**
 * Lo más pequeño que se deja, en proporción al tamaño de la hoja de estilos.
 *
 * Bajo a propósito. El suelo existe para que un valor absurdo no pinte letra de
 * cuatro píxeles, pero **recortar dígitos de un importe es mentir**: `4.898.00`
 * se lee como una cifra y es otra. Entre una cifra pequeña y una cifra falsa,
 * pequeña. `overflow: hidden` queda como último recurso para que nada se salga
 * de la tarjeta, no como la forma normal de resolver esto.
 */
const MINIMO = 0.3;

/**
 * Un pelo de margen. Medir y ajustar al milímetro deja cifras que rozan el
 * borde, y un redondeo del navegador en la dirección mala las saca.
 */
const HOLGURA = 0.98;

/**
 * @param {HTMLElement} cifra
 */
/** Pasadas como mucho. Con dos suele bastar; el tope es para no colgar nada. */
const PASADAS = 8;

/**
 * Encoge una cifra hasta que quepa y devuelve el tamaño en el que quedó.
 *
 * @param {HTMLElement} cifra
 * @returns {number} el tamaño final en píxeles, o 0 si no se pudo medir
 */
function ajustar(cifra) {
  // Al tamaño de la hoja de estilos antes de medir: si no, cada pasada encoge
  // sobre lo ya encogido y el número se va haciendo pequeño para siempre.
  cifra.style.fontSize = '';

  if (!cifra.clientWidth) return 0;

  let tamano = parseFloat(window.getComputedStyle(cifra).fontSize);

  if (!tamano) return 0;

  const minimo = tamano * MINIMO;

  // **A pasadas y no de una.** La cuenta de una vez -«ocupa un 20 % de más,
  // pues un 20 % menos de letra»- se queda corta: el ancho de un texto no baja
  // en proporción exacta al tamaño, que hay redondeos de rasterizado y espacios
  // entre letras que no encogen igual. Con dos pasadas suele estar; el tope es
  // para que esto no pueda quedarse dando vueltas.
  //
  // `scrollWidth` es lo que ocuparía sin cortarse, y vale porque el CSS del
  // componente prohíbe partir la línea: sin eso el número se envuelve y mediría
  // lo mismo que la caja, que es como no medir.
  for (let pasada = 0; pasada < PASADAS; ++pasada) {
    const ancho = cifra.scrollWidth;
    const caja = cifra.clientWidth;

    if (ancho <= caja || tamano <= minimo) return tamano;

    tamano = Math.max(minimo, (tamano * caja * HOLGURA) / ancho);
    cifra.style.fontSize = `${tamano.toFixed(2)}px`;
  }

  return tamano;
}

/**
 * A qué grupo pertenece, si es que pertenece a alguno.
 *
 * @param {HTMLElement} cifra
 * @returns {string}
 */
function grupoDe(cifra) {
  return cifra.dataset.companyCifra || '';
}

/**
 * Ajusta todas, y **las de un mismo grupo acaban del mismo tamaño**.
 *
 * ## Por qué el grupo
 *
 * Dos tarjetas una al lado de la otra con «Pendiente de pago» y «Vencido» son
 * la misma cosa dicha dos veces, y si una cifra sale a 19 px y la otra a 23 se
 * lee que una importa más que la otra. Lo que las diferencia es cuántos dígitos
 * trajo cada una ese día, que no significa nada.
 *
 * Manda **la más pequeña**: es la única que cabe en todas. Al revés -subir las
 * demás a la mayor- volvería a sacar de su caja justo a la que no cabía.
 *
 * @param {Iterable<HTMLElement>} cifras
 */
function ajustarLas(cifras) {
  const todas = [...cifras].filter((cifra) => cifra.isConnected);
  const tamanos = new Map();

  todas.forEach((cifra) => tamanos.set(cifra, ajustar(cifra)));

  /** @type {Map<string, number>} */
  const menores = new Map();

  todas.forEach((cifra) => {
    const grupo = grupoDe(cifra);
    const tamano = tamanos.get(cifra);

    if ('' === grupo || !tamano) return;

    if (!menores.has(grupo) || tamano < menores.get(grupo)) {
      menores.set(grupo, tamano);
    }
  });

  todas.forEach((cifra) => {
    const menor = menores.get(grupoDe(cifra));

    // Bajar siempre cabe: la que ya cabía a 23 cabe a 19. Por eso se puede
    // igualar sin volver a medir.
    if (menor && menor < tamanos.get(cifra)) {
      cifra.style.fontSize = `${menor.toFixed(2)}px`;
    }
  });
}

/** Repasa todas las montadas. Son dos o tres por pantalla: sale gratis. */
function repasar() {
  ajustarLas(montadas);
}

/** @type {?ResizeObserver} */
let midiendo = null;

/** Las que ya están montadas, para volver a medirlas cuando algo cambie. */
const montadas = new Set();

/** El último ancho visto de cada caja vigilada. Ver `elMedidor()`. */
const anchos = new WeakMap();

/**
 * El aviso de «las fuentes ya están» se pide **una vez y para todas**.
 *
 * Pedirlo dentro del montaje parecía lo natural y colgaba el navegador: cada
 * pasada registraba otro `then` que volvía a llamar al montaje, que registraba
 * otro. La promesa ya está resuelta, así que eso no es una espera: es un bucle
 * de microtareas que no suelta el hilo.
 */
let avisadoDeLasFuentes = false;

function alCargarLasFuentes() {
  if (avisadoDeLasFuentes) return;
  if (!document.fonts || typeof document.fonts.ready?.then !== 'function') return;

  avisadoDeLasFuentes = true;

  document.fonts.ready.then(() => repasar());
}

function elMedidor() {
  if (midiendo || typeof ResizeObserver !== 'function') return midiendo;

  // **Se vigila la caja, no la cifra.** Vigilando la cifra, cambiarle el tamaño
  // de letra le cambia el alto, el observador se despierta por su propio
  // cambio, y el navegador acaba tirando avisos por bucle -«ResizeObserver loop
  // completed with undelivered notifications»- dejando el ajuste a medias. La
  // caja no cambia porque la letra encoja.
  // **Y solo cuando cambia el ancho.** Encoger la letra cambia el alto de la
  // tarjeta, y el alto llega por aquí igual que el ancho: sin esta comprobación
  // el propio ajuste despierta al observador, que vuelve a ajustar, que vuelve
  // a cambiar el alto. El ancho no lo decide el contenido -lo decide la
  // rejilla- así que es el único cambio que obliga a volver a medir.
  midiendo = new ResizeObserver((entradas) => {
    const cambio = entradas.some((entrada) => {
      const ancho = Math.round(entrada.contentRect.width);
      const antes = anchos.get(entrada.target);

      anchos.set(entrada.target, ancho);

      return antes !== ancho;
    });

    if (cambio) repasar();
  });

  return midiendo;
}

/**
 * Monta las cifras que haya dentro de `raiz`.
 *
 * @param {ParentNode} [raiz]
 */
export function cuidarLasCifras(raiz = document) {
  if (!raiz || typeof raiz.querySelectorAll !== 'function') return;

  raiz.querySelectorAll('.company-cifra').forEach((cifra) => {
    if (cifra.dataset.companyCifraMontada) return;

    cifra.dataset.companyCifraMontada = '1';
    montadas.add(cifra);

    const medidor = elMedidor();

    if (medidor && cifra.parentElement) medidor.observe(cifra.parentElement);

    // El número puede cambiar sin que cambie la caja -un resumen que se
    // recalcula, una tabla que se refresca- y entonces nadie mediría. Se repasa
    // el grupo entero: si esta se hace larga, las de al lado tienen que bajar
    // con ella.
    if (typeof MutationObserver === 'function') {
      new MutationObserver(() => repasar()).observe(cifra, {
        characterData: true,
        childList: true,
        subtree: true,
      });
    }
  });

  // Una vez montadas todas, se miden juntas: para igualar un grupo hay que
  // haber visto a todos sus miembros.
  repasar();

  // **Cuando acaben de cargar las fuentes.** La primera medida se toma con la
  // de reserva, y Montserrat no ocupa lo mismo: sin esto la cifra queda
  // ajustada a una fuente que ya no está en la pantalla.
  alCargarLasFuentes();
}

export default cuidarLasCifras;
